import type { Locale } from "@porsche-customer/slfinrtl-context-types"
import type { ComponentType } from "react"
import { useMemo } from "react"
import type { RouteObject, RouterProviderProps } from "react-router"
import { Navigate, Outlet, createBrowserRouter, useRouteError } from "react-router"
import { dealerAppRoute } from "src/common/routes"
import type { DealerAppRoute } from "src/common/routes/route"
import { useDealerAppRoutes } from "src/common/routes/useDealerAppRoutes"
import type { VehicleDetailsTabs } from "src/common/routes/useDealerAppRoutes"
import {
	AccountSettingsContainer,
	AddContractualAgreementContainer,
	AddExtraCostSpecificationContainer,
	AddOfferContainer,
	AnalyticsContainer,
	BuildsheetsContainer,
	ContractualAgreementDetailsContainer,
	ContractualAgreementsOverviewContainer,
	CustomerChangeRequestDetailsContainer,
	DealerContactDetailsSettingsContainer,
	DealerFeesAndAccessoriesContainer,
	DeliveredVehicleListContainer,
	EditDosOfferContainer,
	EditOfferContainer,
	ExtraCostSpecificationContainer,
	ExtraCostsOverviewContainer,
	FulfillmentDeliveredVehiclesListContainer,
	InquiryContextContainer,
	InventoryIntegrationMonitorContainer,
	InventoryLinkListContainer,
	MediaEditContainer,
	NewDeliveredVehiclesListContainer,
	NotificationCenterContainer,
	NotificationSettingsContainer,
	OffbrandInventoryListContainer,
	OfferDetailsContainer,
	OfferOverviewContainer,
	OnlineOrderListContainer,
	PurchaseRequestDetailsContainer,
	PurchaseRequestListContainer,
	PurchaseRequestsContainer,
	RemoveVehicleContainer,
	ReportDeliveryContainer,
	ReportSaleForOfferContainer,
	ReservationContainer,
	SaleDetailsContainer,
	SaleDetailsOverviewContainer,
	SaleDetailsSaleHistoryContainer,
	SalesContainer,
	SalesListContainer,
	SaveIntoContextContainer,
	TradeInRequestDetailsContainer,
	TradeInRequestListContainer,
	TransferVehicleContainer,
	UpdateSaleForOfferContainer,
	UsedDeliveredVehiclesListContainer,
	VehicleAddContainer,
	VehicleDeliveryContainer,
	VehicleDetailsBuildsheetContainer,
	VehicleDetailsVehicleHistoryContainer,
	VehicleHistoryContainer,
	VehicleInformationBuildsheetScreenContainer,
	VehicleInformationContainer,
	VehicleInformationMonroneyLabelScreenContainer,
	VehicleInformationVehicleHistoryScreenContainer,
	VehicleMediaListContainer,
} from "src/lazyContainers"
import { HomeContainer } from "src/screens/home/HomeContainer"
import { InventoryListContainer } from "src/screens/inventoryList/InventoryListContainer"
import { VehicleDetailsContainer } from "src/screens/vehicleDetails/VehicleDetailsContainer"
import { OverviewContainer } from "src/screens/vehicleDetails/overviewContainer/OverviewContainer"
import { SalesActivityContainer } from "src/screens/vehicleDetails/salesActivityContainer/SalesActivityContainer"
import { Root } from "../Root"
import { DealerMainNavigation } from "../dealerMainNavigation/DealerMainNavigation"
import { LegacyPurchaseRequestDetailsRedirect } from "./LegacyPurchaseRequestDetailsRedirect"
import { LegacyVehicleDetailsRedirect } from "./LegacyVehicleDetailsRedirect"

const toArray = (path: string | undefined, Component: ComponentType) => (path ? [{ Component, path }] : [])

// tabs children shared by every place vehicleDetails is reachable from - see VehicleDetailsTabs.
const getVehicleDetailsTabs = (vehicleDetailsRoute: DealerAppRoute & VehicleDetailsTabs): RouteObject[] => [
	{
		Component: OverviewContainer,
		index: true,
	},
	...toArray(vehicleDetailsRoute.sales?.path, SalesActivityContainer),
	...toArray(vehicleDetailsRoute.buildsheet?.path, VehicleDetailsBuildsheetContainer),
	{
		Component: VehicleDetailsVehicleHistoryContainer,
		path: vehicleDetailsRoute.vehicleHistory.path,
	},
]

export const useDealerAppRouter = (dealershipId: string, locale: Locale): RouterProviderProps["router"] | null => {
	const routes = useDealerAppRoutes()

	return useMemo(
		() =>
			routes === null
				? null
				: createBrowserRouter(
						[
							{
								element: (
									<Root>
										<DealerMainNavigation routes={routes} />
										<Outlet />
									</Root>
								),
								path: "/",

								ErrorBoundary: () => {
									const error = useRouteError()

									if (error instanceof Error) {
										// Re-throw the error to be caught by the Global Error Boundary
										throw error
									}
								},

								children: [
									...toArray(routes.home?.path, HomeContainer),
									...(routes.inventoryList
										? [
												{
													Component: InventoryListContainer,
													path: routes.inventoryList.path,
												},
												{
													Component: VehicleDetailsContainer,
													path: routes.inventoryList.vehicleDetails.path,

													// tabs
													children: getVehicleDetailsTabs(
														routes.inventoryList.vehicleDetails,
													),
												},

												// action-flow urls
												...toArray(
													routes.inventoryList.vehicleDetails.add.path,
													VehicleAddContainer,
												),
												...toArray(
													routes.inventoryList.vehicleDetails.deliver.path,
													VehicleDeliveryContainer,
												),
												...toArray(
													routes.inventoryList.vehicleDetails.editOffer.path,
													EditDosOfferContainer,
												),
												...toArray(
													routes.inventoryList.vehicleDetails.mediaEdit.path,
													MediaEditContainer,
												),
												...toArray(
													routes.inventoryList.vehicleDetails.remove.path,
													RemoveVehicleContainer,
												),
												...toArray(
													routes.inventoryList.vehicleDetails.reserve.path,
													ReservationContainer,
												),
												...toArray(
													routes.inventoryList.vehicleDetails.sell?.path,
													SalesContainer,
												),
												...toArray(
													routes.inventoryList.vehicleDetails.transfer.path,
													TransferVehicleContainer,
												),

												// legacy top-level /vehicle/:vehicleKey/... URLs, redirected to their new nested location
												{
													Component: LegacyVehicleDetailsRedirect,
													path: "/vehicle/:vehicleKey/*",
												},
											]
										: []),
									...toArray(routes.offbrandInventoryList?.path, OffbrandInventoryListContainer),

									...(routes.salesList
										? [
												{
													Component: SalesListContainer,
													path: routes.salesList.path,
												},
												{
													Component: SaleDetailsContainer,
													path: routes.salesList.saleDetails.path,

													children: [
														{
															Component: SaleDetailsOverviewContainer,
															index: true,
														},
														{
															Component: SaleDetailsSaleHistoryContainer,
															path: routes.salesList.saleDetails.saleHistory.path,
														},
													],
												},
												{
													Component: ReportDeliveryContainer,
													path: routes.salesList.saleDetails.deliver.path,
												},
											]
										: []),
									...(routes.fulfillmentDeliveredVehiclesList
										? [
												{
													Component: FulfillmentDeliveredVehiclesListContainer,
													path: routes.fulfillmentDeliveredVehiclesList.path,

													children: [
														{
															Component: NewDeliveredVehiclesListContainer,
															index: true,
														},
														{
															Component: UsedDeliveredVehiclesListContainer,
															path: routes.fulfillmentDeliveredVehiclesList.usedVehicles
																.path,
														},
													],
												},
												{
													Component: SaleDetailsContainer,
													path: routes.fulfillmentDeliveredVehiclesList.saleDetails.path,

													children: [
														{
															Component: SaleDetailsOverviewContainer,
															index: true,
														},
														{
															Component: SaleDetailsSaleHistoryContainer,
															path: routes.fulfillmentDeliveredVehiclesList.saleDetails
																.saleHistory.path,
														},
													],
												},
												{
													Component: VehicleDetailsContainer,
													path: routes.fulfillmentDeliveredVehiclesList.usedVehicles
														.vehicleDetails.path,

													// tabs
													children: getVehicleDetailsTabs(
														routes.fulfillmentDeliveredVehiclesList.usedVehicles
															.vehicleDetails,
													),
												},
											]
										: []),
									...(routes.deliveredVehiclesList?.path
										? [
												{
													Component: DeliveredVehicleListContainer,
													path: routes.deliveredVehiclesList.path,
												},
												{
													Component: VehicleDetailsContainer,
													path: routes.deliveredVehiclesList.vehicleDetails.path,

													// tabs
													children: getVehicleDetailsTabs(
														routes.deliveredVehiclesList.vehicleDetails,
													),
												},
											]
										: []),
									...toArray(routes.onlineReservations?.path, OnlineOrderListContainer),
									...(routes.tradeInRequests
										? [
												{
													Component: TradeInRequestListContainer,
													path: routes.tradeInRequests.path,
												},
												{
													Component: TradeInRequestDetailsContainer,
													path: routes.tradeInRequests.details.path,
												},
											]
										: []),
									...(routes.purchaseRequests
										? [
												{
													Component: PurchaseRequestsContainer,
													path: routes.purchaseRequests.path,

													children: [
														{
															Component: PurchaseRequestListContainer,
															index: true,
														},
														{
															Component: PurchaseRequestListContainer,
															path: routes.purchaseRequests.withOwnOffers.path,
														},

														{
															Component: PurchaseRequestListContainer,
															path: routes.purchaseRequests.hidden.path,
														},
													],
												},
												{
													Component: PurchaseRequestDetailsContainer,
													path: routes.purchaseRequests.details.path,
												},

												// legacy /purchase-requests/details/:id URL, redirected to its new flat location
												{
													Component: LegacyPurchaseRequestDetailsRedirect,
													path: "/purchase-requests/details/:id",
												},
											]
										: []),
									...toArray(routes.analytics?.path, AnalyticsContainer),
									...(routes.notificationCenter
										? [
												...toArray(routes.notificationCenter.path, NotificationCenterContainer),
												...toArray(
													routes.notificationCenter.group?.path,
													NotificationCenterContainer,
												),
											]
										: []),
									...toArray(routes.inquiryContext?.path, InquiryContextContainer),
									...toArray(
										routes.customerChangeRequestDetails?.path,
										CustomerChangeRequestDetailsContainer,
									),
									// TODO FRD-4711 remove after Salesforce PoC
									...toArray(routes.salesforcePoc?.path, SaveIntoContextContainer),

									// Offers
									...(routes.offers
										? [
												...toArray(routes.offers.offerOverview?.path, OfferOverviewContainer),
												...toArray(routes.offers.addOffer?.path, AddOfferContainer),
												...toArray(routes.offers.offerDetails?.path, OfferDetailsContainer),
												...toArray(routes.offers.editOffer?.path, EditOfferContainer),
												...toArray(
													routes.offers.reportSaleForOffer?.path,
													ReportSaleForOfferContainer,
												),
												...toArray(
													routes.offers.updateSaleForOffer?.path,
													UpdateSaleForOfferContainer,
												),
											]
										: []),

									// Settings
									...(routes.settings
										? [
												...toArray(
													routes.settings.inventoryLinks?.path,
													InventoryLinkListContainer,
												),
												...toArray(
													routes.settings.extraCosts?.path,
													ExtraCostsOverviewContainer,
												),
												...toArray(
													routes.settings.extraCosts?.add?.path,
													AddExtraCostSpecificationContainer,
												),
												...toArray(
													routes.settings.extraCosts?.specification?.path,
													ExtraCostSpecificationContainer,
												),

												...toArray(
													routes.settings.contractualAgreements?.path,
													ContractualAgreementsOverviewContainer,
												),
												...toArray(
													routes.settings.contractualAgreements?.add?.path,
													AddContractualAgreementContainer,
												),
												...toArray(
													routes.settings.contractualAgreements?.details?.path,
													ContractualAgreementDetailsContainer,
												),

												...toArray(routes.settings.account?.path, AccountSettingsContainer),
												...toArray(
													routes.settings.dealerContactDetails?.path,
													DealerContactDetailsSettingsContainer,
												),
												...toArray(
													routes.settings.dealerFeesAndAccessories?.path,
													DealerFeesAndAccessoriesContainer,
												),
												...toArray(
													routes.settings.notificationSettings?.path,
													NotificationSettingsContainer,
												),
											]
										: []),

									...toArray(routes.buildsheets?.path, BuildsheetsContainer),
									...toArray(
										routes.inventoryIntegrationMonitor?.path,
										InventoryIntegrationMonitorContainer,
									),
									...toArray(routes.vehicleHistory?.path, VehicleHistoryContainer),
									...(routes.vehicleInformation?.path
										? [
												{
													Component: VehicleInformationContainer,
													path: routes.vehicleInformation.path,

													children: [
														{
															Component: VehicleInformationBuildsheetScreenContainer,
															index: true,
														},
														...toArray(
															routes.vehicleInformation.monroneyLabel?.path,
															VehicleInformationMonroneyLabelScreenContainer,
														),
														...toArray(
															routes.vehicleInformation.vehicleHistory?.path,
															VehicleInformationVehicleHistoryScreenContainer,
														),
													],
												},
											]
										: []),

									{
										Component: VehicleMediaListContainer,
										path: routes.mediaList.path,
									},
									{
										Component: MediaEditContainer,
										path: routes.mediaList.vehicleMediaEdit.path,
									},

									{
										element: (
											<Navigate
												to={
													routes?.home?.url() ??
													routes?.inventoryList?.url() ??
													routes?.mediaList.url()
												}
											/>
										),
										path: "/*?",
									},
								],
							},
						],
						{
							basename: dealerAppRoute.url(dealershipId, locale),
						},
					),
		[routes, dealershipId, locale],
	)
}
